import { useId, useState, type ReactElement } from "react";
import { Plus, X } from "lucide-react";
import { Button, Input, Switch } from "../atoms/index.js";
import { ProposalReviewValue } from "./ProposalReviewValue.js";
import { ProposalReviewTextArea } from "./ProposalReviewTextArea.js";
import { Select } from "../molecules/index.js";
import { cn } from "../lib/utils.js";
import type {
  ProposalReviewField,
  ProposalReviewFormModel,
} from "./proposal-review.types.js";

/**
 * The friendly form half of the review dialog.
 *
 * One renderer per control, chosen from a closed vocabulary — never a raw JSON
 * or raw-schema editor. Two rules hold throughout:
 *
 *  - **Design-system components only.** No `<select>`, no `<input type=...>`
 *    outside the DS atoms. A native control here would skip the token palette,
 *    the focus ring and the dark/light/high-contrast themes at once.
 *  - **Labels sit in a gutter, and are real `<label>`s.** Every control carries
 *    an explicit `id` with a matching `htmlFor`, so the accessible name is the
 *    visible label and an error is associated rather than merely adjacent.
 */

const LABEL_COLUMN = "w-32 shrink-0 pt-2 text-label text-pb-text-secondary";

/** Render the host's draft, preserving field-level and dialog-level edit authority. */
export function ProposalReviewForm({
  model,
  readOnly,
}: {
  model: ProposalReviewFormModel;
  readOnly: boolean;
}): ReactElement {
  return (
    <div className="flex flex-col gap-3">
      {model.fields.map((field) => (
        <FieldRow
          key={field.path}
          field={field}
          value={model.values[field.path]}
          onChange={(value) => model.onChange(field.path, value)}
          readOnly={readOnly || !field.editable}
        />
      ))}
    </div>
  );
}

/** Associate each field's visible label and validation message with its control. */
function FieldRow({
  field,
  value,
  onChange,
  readOnly,
}: {
  field: ProposalReviewField;
  value: unknown;
  onChange: (value: unknown) => void;
  readOnly: boolean;
}): ReactElement {
  const controlId = useId();
  const errorId = `${controlId}-error`;
  return (
    <div className="flex items-start gap-3">
      <label htmlFor={controlId} className={LABEL_COLUMN}>
        {field.label}
        {field.required ? (
          // Presentational only — the asterisk is announced by the required
          // control itself, so repeating it here would double-announce.
          <span aria-hidden className="ms-1 text-pb-red-text">
            *
          </span>
        ) : null}
      </label>
      <div className="min-w-0 flex-1">
        <FieldControl
          field={field}
          value={value}
          onChange={onChange}
          readOnly={readOnly}
          controlId={controlId}
          errorId={field.error ? errorId : undefined}
        />
        {field.error ? (
          <p
            id={errorId}
            role="alert"
            className="mt-1 text-dense-2 text-pb-red-text"
          >
            {field.error}
          </p>
        ) : null}
      </div>
    </div>
  );
}

/** Render the descriptor's editor or fixed value without granting extra editability. */
function FieldControl({
  field,
  value,
  onChange,
  readOnly,
  controlId,
  errorId,
}: {
  field: ProposalReviewField;
  value: unknown;
  onChange: (value: unknown) => void;
  readOnly: boolean;
  controlId: string;
  errorId: string | undefined;
}): ReactElement {
  if (
    field.control === "textarea" &&
    (typeof value === "string" || (!readOnly && value == null))
  ) {
    return (
      <ProposalReviewTextArea
        field={field}
        value={typeof value === "string" ? value : ""}
        onChange={onChange}
        readOnly={readOnly}
        controlId={controlId}
        errorId={errorId}
      />
    );
  }
  // A non-editable field renders as TEXT, not a disabled control: a disabled
  // input reads as "temporarily unavailable", and these are permanently fixed.
  if (readOnly) {
    return (
      <div
        id={controlId}
        className="rounded-md border border-pb-border bg-pb-shell/60 px-2.5 py-2 text-dense-1 text-pb-text-muted"
      >
        <ProposalReviewValue value={value} />
      </div>
    );
  }

  const invalid = field.error !== undefined;

  switch (field.control) {
    case "textarea":
      return <ProposalReviewValue value={value} />;
    case "number":
      return (
        <Input
          id={controlId}
          type="number"
          value={typeof value === "number" ? String(value) : ""}
          onChange={(event) =>
            // An empty box is ABSENT, not zero. Coercing "" to 0 would silently
            // write a real value the user did not choose.
            onChange(
              event.target.value === "" ? null : Number(event.target.value),
            )
          }
          {...(field.constraints?.min !== undefined
            ? { min: field.constraints.min }
            : {})}
          {...(field.constraints?.max !== undefined
            ? { max: field.constraints.max }
            : {})}
          aria-invalid={invalid || undefined}
          aria-describedby={errorId}
        />
      );
    case "toggle":
      return (
        <Switch
          id={controlId}
          checked={value === true}
          onChange={(checked) => onChange(checked)}
          aria-describedby={errorId}
        />
      );
    case "select":
      return (
        <Select
          id={controlId}
          options={field.options ?? []}
          value={typeof value === "string" ? value : ""}
          onChange={(next) => onChange(next)}
        />
      );
    case "items":
      return (
        <ItemsControl
          controlId={controlId}
          values={Array.isArray(value) ? value.map(String) : []}
          onChange={onChange}
          max={field.constraints?.max}
          label={field.label}
        />
      );
    case "text":
    default:
      return (
        <Input
          id={controlId}
          value={typeof value === "string" ? value : ""}
          onChange={(event) => onChange(event.target.value)}
          {...(field.constraints?.maxLength !== undefined
            ? { maxLength: field.constraints.maxLength }
            : {})}
          {...(field.constraints?.maxLength !== undefined
            ? {
                suffix: (
                  <span className="text-dense-2 text-pb-text-muted tabular-nums">
                    {typeof value === "string" ? value.length : 0}/
                    {field.constraints.maxLength}
                  </span>
                ),
              }
            : {})}
          aria-invalid={invalid || undefined}
          aria-describedby={errorId}
        />
      );
  }
}

/**
 * The repeatable-item control: chips with a remove each, plus an add row.
 *
 * Bounded by `max` — the descriptor's own cap — so a user cannot build a list
 * the server will reject on submit. The add control disables at the cap rather
 * than accepting and truncating, which would discard typing without saying so.
 */
function ItemsControl({
  controlId,
  values,
  onChange,
  max,
  label,
}: {
  controlId: string;
  values: string[];
  onChange: (value: unknown) => void;
  max: number | undefined;
  label: string;
}): ReactElement {
  const atCap = max !== undefined && values.length >= max;
  const [draft, setDraft] = useState("");
  return (
    <Input
      id={controlId}
      value={draft}
      onChange={(event) => setDraft(event.target.value)}
      placeholder={atCap ? "Limit reached" : "Add and press Enter"}
      disabled={atCap}
      prefix={
        values.length > 0 ? (
          <ul className="flex max-w-[30rem] items-center gap-1.5 overflow-hidden">
            {values.map((entry, index) => (
              <li
                key={`${entry}-${index}`}
                className="inline-flex min-w-0 items-center gap-1 rounded bg-pb-card-elevated px-1.5 py-0.5 text-dense-2 text-pb-text"
              >
                <span className="max-w-32 truncate">{entry}</span>
                <Button
                  variant="ghost"
                  size="sm"
                  iconOnly
                  aria-label={`Remove ${entry} from ${label}`}
                  onClick={() =>
                    onChange(values.filter((_, position) => position !== index))
                  }
                  className="h-4 w-4 shrink-0 text-pb-text-muted hover:text-pb-text"
                >
                  <X className="h-3 w-3" aria-hidden />
                </Button>
              </li>
            ))}
          </ul>
        ) : undefined
      }
      onKeyDown={(event) => {
        if (event.key !== "Enter") return;
        event.preventDefault();
        const next = draft.trim();
        if (next === "" || values.includes(next)) return;
        onChange([...values, next]);
        setDraft("");
      }}
    />
  );
}

/** The trailing destructive-ish action under the form. */
export function ProposalReviewFormFooter({
  model,
  readOnly,
}: {
  model: ProposalReviewFormModel;
  readOnly: boolean;
}): ReactElement | null {
  if (model.onRemoveSelected === undefined || readOnly) return null;
  return (
    <div className={cn("mt-4 border-t border-pb-border pt-3")}>
      <Button
        variant="ghost"
        size="sm"
        onClick={model.onRemoveSelected}
        className="text-pb-text-secondary hover:text-pb-red-text"
      >
        <Plus className="h-3.5 w-3.5 rotate-45" aria-hidden />
        {model.removeSelectedLabel ?? "Remove from suggestion"}
      </Button>
    </div>
  );
}
